/**
 * AI Assistant Context Provider
 * 
 * Provides context awareness for the Synthora AI Assistant.
 * Tracks user's current screen state, active elements, and recent actions
 * to enable intelligent, contextual assistance.
 * 
 * All conversations are persisted to the database for analytics.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

// Generate a unique session ID
const generateSessionId = () => {
  return `synthora-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Default settings
const DEFAULT_SETTINGS = {
  proactiveSuggestions: true,
  elementExplanations: true,
  autoGenerateEnabled: true,
  suggestionFrequency: 'sometimes',
  suggestionTypes: {
    nextSteps: true,
    tips: true,
    warnings: true
  },
  explanationDetail: 'brief',
  showShortcuts: true,
  autoGenerateTypes: {
    flows: true,
    rules: true,
    directives: true
  },
  confirmBeforeGenerate: true
};

// ============================================
// CONTEXT DEFINITION
// ============================================

const AIAssistantContext = createContext(null);

// ============================================
// CONTEXT PROVIDER
// ============================================

export const AIAssistantProvider = ({ children }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  
  // Panel visibility state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  
  // Session ID for conversation persistence
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  
  // Current screen context - what the user is looking at
  const [screenContext, setScreenContext] = useState({
    currentModule: null,           // e.g., 'agent-directives', 'scenario-flows'
    moduleName: null,              // Human-readable name
    moduleDescription: null,       // What this module does
    selectedElements: {},          // Currently selected items (flow, node, ruleSet, etc.)
    formState: {},                 // Current form/input values
    availableActions: [],          // What actions the user can take
    uiState: {},                   // UI-specific state (panels open, tabs selected)
  });
  
  // Recent user actions for context
  const [recentActions, setRecentActions] = useState([]);
  const maxRecentActions = 10;
  
  // Conversation history for the assistant
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Loading state for AI responses
  const [isLoading, setIsLoading] = useState(false);
  
  // Track if conversation needs saving
  const pendingSave = useRef(false);
  const saveTimeout = useRef(null);

  // Track context update timestamps to avoid stale data
  const lastContextUpdate = useRef(Date.now());
  
  // Settings state
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  
  // Proactive suggestions state
  const [proactiveSuggestion, setProactiveSuggestion] = useState(null);
  const [showProactiveSuggestion, setShowProactiveSuggestion] = useState(false);
  
  // Element explanation state
  const [hoveredElement, setHoveredElement] = useState(null);
  const [elementExplanation, setElementExplanation] = useState(null);
  
  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await authenticatedAxios.get('/synthora/assistant/settings');
        if (response.data?.settings) {
          setSettings(prev => ({ ...prev, ...response.data.settings }));
        }
        setSettingsLoaded(true);
      } catch (error) {
        console.error('[AIAssistant] Error loading settings:', error);
        setSettingsLoaded(true);
      }
    };
    
    loadSettings();
  }, [authenticatedAxios]);

  // ============================================
  // CONTEXT UPDATE FUNCTIONS
  // ============================================

  /**
   * Update the current screen context
   * Called by modules when their state changes
   */
  const updateContext = useCallback((newContext) => {
    lastContextUpdate.current = Date.now();
    setScreenContext(prev => ({
      ...prev,
      ...newContext,
      lastUpdated: Date.now()
    }));
  }, []);

  /**
   * Update selected elements in current module
   */
  const updateSelectedElements = useCallback((elements) => {
    setScreenContext(prev => ({
      ...prev,
      selectedElements: {
        ...prev.selectedElements,
        ...elements
      },
      lastUpdated: Date.now()
    }));
  }, []);

  /**
   * Update form/input state
   */
  const updateFormState = useCallback((formData) => {
    setScreenContext(prev => ({
      ...prev,
      formState: {
        ...prev.formState,
        ...formData
      }
    }));
  }, []);

  /**
   * Record a user action for context
   */
  const recordAction = useCallback((action) => {
    const actionWithTimestamp = {
      ...action,
      timestamp: Date.now()
    };
    
    setRecentActions(prev => {
      const updated = [actionWithTimestamp, ...prev];
      return updated.slice(0, maxRecentActions);
    });
  }, []);

  /**
   * Clear context when leaving a module
   */
  const clearContext = useCallback(() => {
    setScreenContext({
      currentModule: null,
      moduleName: null,
      moduleDescription: null,
      selectedElements: {},
      formState: {},
      availableActions: [],
      uiState: {},
    });
  }, []);

  // ============================================
  // PANEL CONTROLS
  // ============================================

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    // Don't close if pinned (unless explicitly unpinned)
    if (!isPinned) {
      setIsPanelOpen(false);
    }
  }, [isPinned]);

  const togglePin = useCallback(() => {
    setIsPinned(prev => {
      const newPinned = !prev;
      // If pinning, ensure panel is open
      if (newPinned) {
        setIsPanelOpen(true);
      }
      return newPinned;
    });
  }, []);

  const unpinAndClose = useCallback(() => {
    setIsPinned(false);
    setIsPanelOpen(false);
  }, []);

  // ============================================
  // CONVERSATION PERSISTENCE
  // ============================================

  /**
   * Save conversation to backend (debounced)
   */
  const saveConversation = useCallback(async (messages) => {
    if (!messages || messages.length === 0) return;
    
    try {
      await authenticatedAxios.post('/synthora/assistant/conversation', {
        sessionId,
        messages,
        startContext: {
          currentModule: screenContext.currentModule || 'unknown',
          moduleName: screenContext.moduleName || 'Unknown',
          moduleDescription: screenContext.moduleDescription
        }
      });
      console.log('[AIAssistant] Conversation saved successfully');
    } catch (error) {
      console.error('[AIAssistant] Error saving conversation:', error);
    }
  }, [authenticatedAxios, sessionId, screenContext]);

  /**
   * Debounced save - waits 2 seconds after last message before saving
   */
  const debouncedSave = useCallback((messages) => {
    pendingSave.current = true;
    
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    saveTimeout.current = setTimeout(() => {
      if (pendingSave.current && messages.length > 0) {
        saveConversation(messages);
        pendingSave.current = false;
      }
    }, 2000); // Save 2 seconds after last message
  }, [saveConversation]);

  // ============================================
  // CONVERSATION MANAGEMENT
  // ============================================

  const addMessage = useCallback((message) => {
    setConversationHistory(prev => {
      const updated = [...prev, {
        ...message,
        timestamp: Date.now(),
        context: message.role === 'user' ? {
          currentModule: screenContext.currentModule,
          moduleName: screenContext.moduleName
        } : undefined
      }];
      
      // Trigger debounced save
      debouncedSave(updated);
      
      return updated;
    });
  }, [screenContext, debouncedSave]);

  const clearConversation = useCallback(() => {
    // Save current conversation before clearing (if any messages)
    if (conversationHistory.length > 0) {
      saveConversation(conversationHistory);
    }
    
    // Clear and start new session
    setConversationHistory([]);
    setSessionId(generateSessionId());
    
    // Clear any pending save
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    pendingSave.current = false;
  }, [conversationHistory, saveConversation]);

  // Save conversation when panel closes or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup: save any pending conversation on unmount
      if (conversationHistory.length > 0 && pendingSave.current) {
        saveConversation(conversationHistory);
      }
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  // ============================================
  // SETTINGS MANAGEMENT
  // ============================================

  const updateSettings = useCallback(async (newSettings) => {
    const mergedSettings = { ...settings, ...newSettings };
    setSettings(mergedSettings);
    
    try {
      await authenticatedAxios.put('/synthora/assistant/settings', {
        settings: mergedSettings
      });
      console.log('[AIAssistant] Settings saved');
    } catch (error) {
      console.error('[AIAssistant] Error saving settings:', error);
    }
  }, [settings, authenticatedAxios]);

  const toggleSettingsPanel = useCallback(() => {
    setShowSettingsPanel(prev => !prev);
  }, []);

  // ============================================
  // PROACTIVE SUGGESTIONS
  // ============================================

  /**
   * Generate a proactive suggestion based on current context
   */
  const generateProactiveSuggestion = useCallback(() => {
    if (!settings.proactiveSuggestions || !screenContext.currentModule) {
      setProactiveSuggestion(null);
      return;
    }

    // Generate contextual suggestion based on module and state
    let suggestion = null;
    const module = screenContext.currentModule;
    const elements = screenContext.selectedElements || {};
    
    if (module === 'agent-directives') {
      if (!elements.hasDirective) {
        suggestion = {
          type: 'nextStep',
          text: 'Start by writing your requirements in natural language, then generate a directive.',
          action: 'show_requirements_help'
        };
      } else if (!elements.hasTested) {
        suggestion = {
          type: 'tip',
          text: 'Your directive is ready! Test it with different scenarios to ensure it works correctly.',
          action: 'focus_test_panel'
        };
      }
    } else if (module === 'scenario-flows') {
      if (!elements.selectedFlow) {
        suggestion = {
          type: 'nextStep',
          text: 'Create a new flow or select an existing one to start building your decision tree.',
          action: 'create_flow'
        };
      } else if (elements.nodeCount === 1) {
        suggestion = {
          type: 'tip',
          text: 'Add Question or Condition nodes to build your flow logic. Drag from node handles to connect them.',
          action: 'show_node_help'
        };
      } else if (elements.nodeCount >= 3 && !elements.hasOutcome) {
        suggestion = {
          type: 'warning',
          text: 'Your flow needs at least one Outcome node to complete the decision path.',
          action: 'add_outcome'
        };
      }
    } else if (module === 'rule-sets') {
      if (elements.selectedRuleSet && !elements.hasGeneratedDirective) {
        suggestion = {
          type: 'nextStep',
          text: 'Generate a directive from this rule set to use it with your AI agents.',
          action: 'generate_directive'
        };
      }
    } else if (module === 'knowledge-categories') {
      if (elements.categoryCount === 0) {
        suggestion = {
          type: 'nextStep',
          text: 'Add categories to help the AI classify and route user messages appropriately.',
          action: 'add_category'
        };
      }
    }

    // Apply frequency filter
    if (suggestion && settings.suggestionFrequency === 'minimal') {
      // Only show warnings in minimal mode
      if (suggestion.type !== 'warning') {
        suggestion = null;
      }
    } else if (suggestion && settings.suggestionFrequency === 'sometimes') {
      // Filter based on type settings
      if (suggestion.type === 'nextStep' && !settings.suggestionTypes.nextSteps) suggestion = null;
      if (suggestion.type === 'tip' && !settings.suggestionTypes.tips) suggestion = null;
      if (suggestion.type === 'warning' && !settings.suggestionTypes.warnings) suggestion = null;
    }

    setProactiveSuggestion(suggestion);
    setShowProactiveSuggestion(!!suggestion);
  }, [settings, screenContext]);

  // Generate suggestions when context changes
  useEffect(() => {
    if (settingsLoaded && settings.proactiveSuggestions) {
      const debounce = setTimeout(generateProactiveSuggestion, 500);
      return () => clearTimeout(debounce);
    }
  }, [screenContext, settingsLoaded, settings.proactiveSuggestions, generateProactiveSuggestion]);

  const dismissProactiveSuggestion = useCallback(() => {
    setShowProactiveSuggestion(false);
    setProactiveSuggestion(null);
  }, []);

  // ============================================
  // ELEMENT EXPLANATIONS
  // ============================================

  /**
   * Get explanation for a hovered element
   */
  const getElementExplanation = useCallback(async (elementType, elementData) => {
    if (!settings.elementExplanations) return null;

    // Predefined explanations for common elements
    const explanations = {
      // Agent Directives
      'requirements-input': {
        brief: 'Write natural language instructions for your AI agent here.',
        detailed: 'This is where you describe how you want your AI to behave. Be specific - include examples, tone preferences, and any rules the agent should follow. The AI will convert this into a structured directive.',
        shortcut: 'Ctrl+Enter to generate'
      },
      'directive-display': {
        brief: 'The generated directive that controls AI behavior.',
        detailed: 'This is the structured output that your AI agent will use. It includes personality traits, response guidelines, and specific instructions derived from your requirements.',
        shortcut: null
      },
      'test-panel': {
        brief: 'Test your agent with sample messages.',
        detailed: 'Send test messages to see how your AI agent responds. This helps you refine the directive before deploying it live.',
        shortcut: null
      },
      
      // Scenario Flows
      'flow-canvas': {
        brief: 'Drag and connect nodes to build decision flows.',
        detailed: 'The flow canvas is where you design visual decision trees. Add nodes from the toolbar, then drag from node handles to create connections between them.',
        shortcut: 'Delete to remove selected'
      },
      'node-question': {
        brief: 'Asks the user a question and waits for response.',
        detailed: 'Question nodes prompt the user for input. You can specify the expected answer type (text, yes/no, multiple choice) and the question text.',
        shortcut: null
      },
      'node-condition': {
        brief: 'Branches the flow based on a condition.',
        detailed: 'Condition nodes evaluate a value and route to different paths. They have "Yes" and "No" outputs for true/false results.',
        shortcut: null
      },
      'node-outcome': {
        brief: 'Ends a flow path with a final result.',
        detailed: 'Outcome nodes mark the end of a decision path. They specify the result type (success, failure, pending, escalate) and a message.',
        shortcut: null
      },
      
      // Rule Sets
      'rule-item': {
        brief: 'A single IF-THEN rule for the AI.',
        detailed: 'Each rule contains conditions (IF) and actions (THEN). Rules are evaluated in priority order.',
        shortcut: null
      },
      'generate-directive-btn': {
        brief: 'Convert rules into an AI directive.',
        detailed: 'This takes your structured rules and generates a natural language directive that can be applied to any AI agent.',
        shortcut: null
      },
      
      // Knowledge Categories
      'category-card': {
        brief: 'A topic category for message classification.',
        detailed: 'Categories help the AI understand and route incoming messages. Each category has keywords and scenarios that help with matching.',
        shortcut: null
      }
    };

    const explanation = explanations[elementType];
    if (!explanation) return null;

    return {
      text: settings.explanationDetail === 'detailed' ? explanation.detailed : explanation.brief,
      shortcut: settings.showShortcuts ? explanation.shortcut : null
    };
  }, [settings]);

  const showElementExplanation = useCallback(async (elementType, elementData, position) => {
    if (!settings.elementExplanations) return;
    
    const explanation = await getElementExplanation(elementType, elementData);
    if (explanation) {
      setHoveredElement({ type: elementType, data: elementData, position });
      setElementExplanation(explanation);
    }
  }, [settings.elementExplanations, getElementExplanation]);

  const hideElementExplanation = useCallback(() => {
    setHoveredElement(null);
    setElementExplanation(null);
  }, []);

  // ============================================
  // AUTO-GENERATE CONFIGURATIONS
  // ============================================

  /**
   * Generate configuration from natural language
   */
  const autoGenerate = useCallback(async (type, description) => {
    if (!settings.autoGenerateEnabled) {
      throw new Error('Auto-generate is disabled in settings');
    }

    if (!settings.autoGenerateTypes[type]) {
      throw new Error(`Auto-generate for ${type} is disabled in settings`);
    }

    // The actual generation will be handled by the panel component
    // This function just validates settings
    return {
      canGenerate: true,
      needsConfirmation: settings.confirmBeforeGenerate,
      type,
      description
    };
  }, [settings]);

  // ============================================
  // GET FULL CONTEXT FOR AI
  // ============================================

  /**
   * Get the complete context object to send to AI
   * Includes screen state, recent actions, and conversation history
   */
  const getFullContext = useCallback(() => {
    return {
      screen: screenContext,
      recentActions: recentActions.slice(0, 5), // Last 5 actions
      conversationHistory: conversationHistory.slice(-10), // Last 10 messages
      timestamp: Date.now()
    };
  }, [screenContext, recentActions, conversationHistory]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // State
    isPanelOpen,
    screenContext,
    recentActions,
    conversationHistory,
    isLoading,
    sessionId,
    
    // Context updates
    updateContext,
    updateSelectedElements,
    updateFormState,
    recordAction,
    clearContext,
    
    // Panel controls
    togglePanel,
    openPanel,
    closePanel,
    isPinned,
    togglePin,
    unpinAndClose,
    
    // Conversation
    addMessage,
    clearConversation,
    setIsLoading,
    saveConversation,
    
    // Full context for AI
    getFullContext,
    
    // Settings
    settings,
    settingsLoaded,
    showSettingsPanel,
    updateSettings,
    toggleSettingsPanel,
    
    // Proactive Suggestions
    proactiveSuggestion,
    showProactiveSuggestion,
    dismissProactiveSuggestion,
    
    // Element Explanations
    hoveredElement,
    elementExplanation,
    showElementExplanation,
    hideElementExplanation,
    
    // Auto-Generate
    autoGenerate,
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
};

// ============================================
// CUSTOM HOOK
// ============================================

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }
  return context;
};

export default AIAssistantContext;

